import {ApolloClient} from '@apollo/client';
import type {NormalizedCacheObject} from '@apollo/client';
import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';

import {DeviceType} from '../net/types/graphql';
import type {
  RegisterDeviceMutation,
  RegisterDeviceMutationVariables,
  UnregisterDeviceMutation,
  UnregisterDeviceMutationVariables,
} from '../net/types/graphql';
import {REGISTER_DEVICE} from '../net/query/RegisterDevice.gql';
import {UNREGISTER_DEVICE} from '../net/query/UnregisterDevice.gql';
import {NudgeServiceWorker} from '../bg/NudgeServiceWorker';

/**
 * Interface to the background service worker.
 */
export const sw = new NudgeServiceWorker();

/**
 * Arguments required to register a user's device.
 */
export type RegisterDeviceArgs = {
  client: ApolloClient<NormalizedCacheObject>;
  userId: string;
  deviceId: string;
};

/**
 * Arguments required to unregister a user's device.
 */
export type UnregisterDeviceArgs = RegisterDeviceArgs;

/**
 * Unregister a device from the server.
 */
export const unregisterDevice = createAsyncThunk(
  'device/unregister',
  async ({client, deviceId}: UnregisterDeviceArgs) => {
    // Unregister the device on the server.
    const r = await client.mutate<
      UnregisterDeviceMutation,
      UnregisterDeviceMutationVariables
    >({
      mutation: UNREGISTER_DEVICE,
      variables: {
        deviceId,
      },
    });

    if (r.errors) {
      throw new Error(r.errors.join(', '));
    }
  },
);

/**
 * Register a device for sending messages.
 */
export const registerDevice = createAsyncThunk(
  'device/register',
  async ({client, userId, deviceId}: RegisterDeviceArgs) => {
    if (!deviceId) {
      throw new Error('Device ID is unavailable');
    }

    await sw.waitForActivate();

    // Register the browser as a device for this user on the server.
    const r = await client.mutate<
      RegisterDeviceMutation,
      RegisterDeviceMutationVariables
    >({
      mutation: REGISTER_DEVICE,
      variables: {
        deviceId,
        deviceType: DeviceType.Web,
        ownerId: userId,
        token: '', // DEPRECATED: we don't use FCM-push tokens anymore.
      },
    });

    if (r.errors) {
      throw new Error(r.errors.join(', '));
    }
  },
);

export const deviceSlice = createSlice({
  name: 'device',
  initialState: {
    registering: false,
    deviceId: null as string | null,
    registered: false,
    error: null as string | null,
  },
  reducers: {},
  extraReducers: (builder) => {
    // Register
    builder.addCase(registerDevice.pending, (state) => {
      state.registering = true;
      state.error = null;
      state.registered = false;
      state.deviceId = null;
    });

    builder.addCase(registerDevice.fulfilled, (state, action) => {
      state.registering = false;
      state.error = null;
      state.registered = true;
      state.deviceId = action.meta.arg.deviceId;
    });

    builder.addCase(registerDevice.rejected, (state, action) => {
      state.registering = false;
      state.error = action.error?.message || 'unknown error';
      state.registered = false;
      state.deviceId = null;
    });

    // Unregister
    builder.addCase(unregisterDevice.pending, (state) => {
      state.registering = true;
      state.error = null;
      state.registered = false;
      state.deviceId = null;
    });

    builder.addCase(unregisterDevice.fulfilled, (state) => {
      state.registering = false;
      state.error = null;
      state.registered = false;
      state.deviceId = null;
    });

    builder.addCase(unregisterDevice.rejected, (state, action) => {
      state.registering = false;
      state.error = action.error?.message || 'unknown error';
      state.registered = false;
      state.deviceId = null;
    });
  },
});

export const {reducer} = deviceSlice;
